import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";

const EMAIL = "direct@akramfortas.com";

function EmailLink() {
  const [copied, setCopied] = useState(false);
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      e.preventDefault();
      navigator.clipboard.writeText(EMAIL).then(
        () => {
          setCopied(true);
          toast.success("Email copied", { description: EMAIL });
          setTimeout(() => setCopied(false), 1800);
          window.location.href = `mailto:${EMAIL}`;
        },
        () => {
          window.location.href = `mailto:${EMAIL}`;
        },
      );
    }
  };
  return (
    <a
      className="link-quiet inline-flex items-center gap-2"
      href={`mailto:${EMAIL}`}
      onClick={handleClick}
      aria-label={`${EMAIL} — click to copy and open email`}
    >
      <span>{EMAIL}</span>
      <span className="text-xs text-muted-foreground" aria-hidden="true">
        {copied ? "copied ✓" : "copy"}
      </span>
    </a>
  );
}

export function SiteNav() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-background/70 border-b hairline">
      <nav className="container-prose flex items-center justify-between py-4">
        <Link to="/" className="group flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-full bg-[color:var(--color-leaf)] animate-breathe"
            aria-hidden="true"
          />
          <span className="font-serif text-lg leading-none text-foreground">Akram Fortas</span>
        </Link>
        <ul className="flex items-center gap-5 text-sm text-muted-foreground">
          <li className="hidden sm:block">
            <a href="/#planting" className="link-quiet">Planting</a>
          </li>
          <li className="hidden sm:block">
            <a href="/#lessons" className="link-quiet">Lessons</a>
          </li>
          <li className="hidden md:block">
            <a href="/#journey" className="link-quiet">Journey</a>
          </li>
          <li>
            <Link to="/garden" className="link-quiet">Garden</Link>
          </li>
          <li>
            <Link to="/now" className="link-quiet">Now</Link>
          </li>
          <li>
            <SessionAffordance />
          </li>
        </ul>
      </nav>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="mt-32 border-t hairline">
      <div className="container-prose py-12 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-serif text-3xl text-foreground">Still planting.</p>
          <p className="mt-2 text-sm text-muted-foreground">
            © {new Date().getFullYear()} Akram Fortas. Grown slowly, on purpose.
          </p>
        </div>
        <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <li>
            <a className="link-quiet" href="https://linkedin.com/in/fopreneur" target="_blank" rel="noreferrer noopener">
              LinkedIn ↗
            </a>
          </li>
          <li>
            <a className="link-quiet" href="https://x.com/fopreneur" target="_blank" rel="noreferrer noopener">
              X ↗
            </a>
          </li>
          <li>
            <a className="link-quiet" href="https://instagram.com/fopreneur" target="_blank" rel="noreferrer noopener">
              Instagram ↗
            </a>
          </li>
          <li>
            <EmailLink />
          </li>
        </ul>
      </div>
    </footer>
  );
}

function SessionAffordance() {
  const [email, setEmail] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setEmail(data.session?.user.email ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_ev, session) => {
      setEmail(session?.user.email ?? null);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);
  if (email) {
    return (
      <Link to="/admin" className="link-quiet" title={email}>
        Admin
      </Link>
    );
  }
  return (
    <Link to="/auth" className="link-quiet text-muted-foreground/60">
      ·
    </Link>
  );
}
